import Vue from 'vue';
import { FormField } from '@/models';
import { DirectiveBinding } from 'vue/types/options';

interface FormFieldObjArgs {
  field: FormField;
  onUpdate?: (value: string) => void;
  formatter?: (field: FormField) => string;
  sanitizer?: (value: string, oldValue?: string) => string;
  validator?: (value: string) => void;
}

Vue.directive('form-field', {
  bind: (el, binding) => {
    const { field, onUpdate, formatter, sanitizer, validator } = extractArgsFromBinding(binding);
    let valueMaxLength = 0;

    el.addEventListener('input', e => {
      const ev = e as KeyboardEvent & { target: HTMLInputElement };

      const oldValue = field.oldValue ?? '';
      const target = ev.target;

      const invalidInput = oldValue != '' && target.validity && target.validity.badInput;

      // If the input was invalid, revert to the last known good value
      let value = !invalidInput ? target.value : oldValue || target.value;

      const sanitizedValue = sanitizer ? sanitizer(value, oldValue) : value;

      // If there's a max length, trim the value
      const maxLength = (e.target as HTMLInputElement).maxLength;
      if (maxLength > 0 && sanitizedValue.length > maxLength) {
        value = sanitizedValue.substr(0, maxLength);
      }

      // Update the field value
      field.value = sanitizedValue;
      target.value = sanitizedValue;

      // Pre-emptively set the dirty flag to give the field a chance to display
      // an error message if the sanitizer weeded out bad input
      if (sanitizedValue != value) {
        markDirty(field, el);
      }

      // Save the new value in the dataset
      if (value) {
        field.oldValue = sanitizedValue;
      }

      // Fire onUpdate if possible
      if (value != oldValue && onUpdate) {
        onUpdate(sanitizedValue);
      }

      // Validate the field after update
      if (validator) {
        validator(sanitizedValue);
      }
    });

    el.addEventListener(
      'blur',
      e => {
        const value = (e as any).target.value;

        markDirty(field, el);

        if (validator) {
          validator(value);
        }

        // Format the field in case the field wants to be displayed in a specialized way
        if (formatter) {
          valueMaxLength = (e.target as HTMLInputElement).maxLength;
          if (valueMaxLength > 0) {
            field.value = field.value.substr(0, valueMaxLength);
          }

          const formatted = formatter(field);
          field.formatted = formatted;
          (e.target as HTMLInputElement).maxLength = formatted.length;
        }
      },
      true,
    );

    el.addEventListener(
      'focus',
      e => {
        field.formatted = '';
        if (valueMaxLength > 0) {
          (e.target as HTMLInputElement).maxLength = valueMaxLength;
        }
      },
      true,
    );
  },
  update(el, binding) {
    const { field } = extractArgsFromBinding(binding);

    field.oldValue = field.value;
  },
});

function markDirty(field: FormField, el: Element) {
  if (!field.dirty) {
    field.dirty = true;
    el.classList.add('v-form-field-dirty');
  }
}

function isFormFieldObjArgs(obj: any): obj is FormFieldObjArgs {
  return !!(obj as FormFieldObjArgs).field;
}

function extractArgsFromBinding(binding: DirectiveBinding): FormFieldObjArgs {
  return {
    field: isFormFieldObjArgs(binding.value) ? binding.value.field : <FormField>binding.value,
    onUpdate: binding.value.onUpdate,
    formatter: binding.value.formatter,
    sanitizer: binding.value.sanitizer,
    validator: binding.value.validator,
  };
}
